import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DatePicker, Popover, Progress, Select, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { getNamespaceGatewayGapsApi } from '../../api/customerApi';
import { DECODED_ACCESS_TOKEN } from '../../constants/system.keys';
import storageService from '../../services/storage.service';
import {
  calculateSingleGatewaySLA,
  formatSecondsToHHMMSS,
  getSlaColor,
} from '../../untils/gateway';

const { RangePicker } = DatePicker;
const { Option } = Select;

const GatewaySlaProgress = ({
  gatewaySerial,
  namespace,
  fallbackCameras = 0,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [slaData, setSlaData] = useState(null);

  const [dateRanger, setDateRanger] = useState([
    moment().set({ hour: 0, minute: 0, second: 0 }).valueOf(),
    moment().set({ hour: 23, minute: 59, second: 59 }).valueOf(),
  ]);

  const getSelectedOption = useCallback(() => {
    if (!dateRanger || dateRanger.length !== 2) return undefined;
    const start = moment(dateRanger[0]).startOf('day');
    const end = moment(dateRanger[1]).startOf('day');
    const diffDays = end.diff(start, 'days') + 1;

    if (diffDays === 1) return 1;
    if (diffDays === 3) return 3;
    if (diffDays === 7) return 7;
    if (diffDays >= 28 && diffDays <= 31) return 30;
    return undefined;
  }, [dateRanger]);

  const changeDateRanger = (delta) => {
    const dataFromTime = Math.floor(
      moment()
        .subtract({ days: delta - 1 })
        .set({ hour: 0, minute: 0, second: 0 })
        .valueOf(),
    );
    const dataToTime = Math.floor(
      moment().set({ hour: 23, minute: 59, second: 59 }).valueOf(),
    );
    setDateRanger([dataFromTime, dataToTime]);
  };

  const handleDateRangerChange = (dates) => {
    if (!dates || dates.length !== 2) {
      setDateRanger(null);
    } else {
      const dataFromTime = Math.floor(
        dates[0].set({ hour: 0, minute: 0, second: 0 }).valueOf(),
      );
      const dataToTime = Math.floor(
        dates[1].set({ hour: 23, minute: 59, second: 59 }).valueOf(),
      );
      setDateRanger([dataFromTime, dataToTime]);
    }
  };

  const fromDateStr = useMemo(() => {
    if (!dateRanger || !dateRanger[0]) return moment().format('YYYY-MM-DD');
    return moment(dateRanger[0]).format('YYYY-MM-DD');
  }, [dateRanger]);

  const toDateStr = useMemo(() => {
    if (!dateRanger || !dateRanger[1]) return moment().format('YYYY-MM-DD');
    return moment(dateRanger[1]).format('YYYY-MM-DD');
  }, [dateRanger]);

  useEffect(() => {
    let isMounted = true;

    const fetchGatewaySla = async () => {
      if (!gatewaySerial || !namespace || namespace !== 'hdbank.com.vn') {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const token =
          storageService.getInternalStorageByKey(DECODED_ACCESS_TOKEN);
        const dataQuery = {
          fromDate: fromDateStr,
          toDate: toDateStr,
        };

        const res = await getNamespaceGatewayGapsApi(
          token,
          { namespace },
          dataQuery,
        );

        if (isMounted) {
          const apiData = res?.data?.data || res?.data;
          const gateways =
            apiData?.gateways || (Array.isArray(apiData) ? apiData : []);

          const targetGw = gateways.find(
            (g) => (g.gatewaySerial || g.serial) === gatewaySerial,
          );

          if (targetGw) {
            const calculated = calculateSingleGatewaySLA(
              targetGw,
              dateRanger,
              fallbackCameras,
            );
            setSlaData(calculated);
          } else {
            // Không có gap ghi nhận trong khoảng thời gian này -> mặc định 100%
            setSlaData(
              calculateSingleGatewaySLA(
                { gatewaySerial, totalGapS: 0, totalUploadS: 0 },
                dateRanger,
                fallbackCameras,
              ),
            );
          }
        }
      } catch (error) {
        console.error('Error fetching gateway SLA for progress:', error);
        if (isMounted) {
          setSlaData(
            calculateSingleGatewaySLA(
              {
                gatewaySerial,
                totalGapS: 0,
                totalUploadS: 0,
              },
              dateRanger,
              fallbackCameras,
            ),
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchGatewaySla();

    return () => {
      isMounted = false;
    };
  }, [gatewaySerial, namespace, fallbackCameras, fromDateStr, toDateStr, dateRanger]);

  const percent = slaData?.uploadedPercent ?? 100;
  const strokeColor = getSlaColor(percent);

  const storedPercent = percent;
  const recoverableOverTotalPercent = slaData?.recoverableOverTotalPercent ?? 0;
  const unrecoverableOverTotalPercent =
    slaData?.unrecoverableOverTotalPercent ?? 0;

  const popoverContent = (
    <div
      style={{
        padding: '6px 4px',
        width: '360px',
        maxWidth: '100%',
        fontSize: '12px',
        lineHeight: 1.6,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <div
          style={{
            fontWeight: 'bold',
            fontSize: '13px',
            color: '#60a5fa',
          }}
        >
          {t('SLA Details') || 'Chi tiết SLA'}
        </div>
      </div>

      {/* Date Picker Section */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '12px',
          paddingBottom: '10px',
          borderBottom: '1px solid #374151',
        }}
      >
        <Select
          style={{ width: 105 }}
          size="small"
          value={getSelectedOption()}
          onChange={(val) => val && changeDateRanger(val)}
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          <Option value={1}>{t('Today')}</Option>
          <Option value={3}>{t('3 days')}</Option>
          <Option value={7}>{t('7 days')}</Option>
          <Option value={30}>{t('30 days')}</Option>
        </Select>
        <RangePicker
          style={{ flex: 1 }}
          size="small"
          value={
            dateRanger && dateRanger.length === 2
              ? [moment(dateRanger[0]), moment(dateRanger[1])]
              : []
          }
          onChange={handleDateRangerChange}
          disabledDate={(current) =>
            current && current.isAfter(moment(), 'day')
          }
          getCalendarContainer={(trigger) => trigger.parentNode}
        />
      </div>

      {/* 1. Stored */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '6px',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#10b981',
              display: 'inline-block',
            }}
          />
          <span>{t('Stored') || 'Stored'}:</span>
        </span>
        <span>
          <strong style={{ color: '#10b981', marginRight: '6px' }}>
            {storedPercent}%
          </strong>
          <span style={{ color: '#9ca3af' }}>
            ({formatSecondsToHHMMSS(slaData?.uploadedDurationSeconds || 0)})
          </span>
        </span>
      </div>

      {/* 2. Recoverable */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '6px',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#f59e0b',
              display: 'inline-block',
            }}
          />
          <span>
            {t('Missing - Recoverable') || 'Missing - Recoverable'}:
          </span>
        </span>
        <span>
          <strong style={{ color: '#f59e0b', marginRight: '6px' }}>
            {recoverableOverTotalPercent}%
          </strong>
          <span style={{ color: '#9ca3af' }}>
            ({formatSecondsToHHMMSS(slaData?.totalUploadS || 0)})
          </span>
        </span>
      </div>

      {/* 3. Unrecoverable */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#ef4444',
              display: 'inline-block',
            }}
          />
          <span>
            {t('Missing - Unrecoverable') || 'Missing - Unrecoverable'}:
          </span>
        </span>
        <span>
          <strong style={{ color: '#ef4444', marginRight: '6px' }}>
            {unrecoverableOverTotalPercent}%
          </strong>
          <span style={{ color: '#9ca3af' }}>
            ({formatSecondsToHHMMSS(slaData?.unrecoverableDurationS || 0)})
          </span>
        </span>
      </div>
    </div>
  );

  if (namespace !== 'hdbank.com.vn') return null;

  return (
    <div className="progress sla-progress">
      <Popover
        content={popoverContent}
        placement="top"
        trigger={['hover', 'click']}
        overlayClassName="gateway-sla-popover"
        overlayStyle={{ width: '380px', maxWidth: '90vw' }}
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
      >
        <div style={{ cursor: 'pointer', display: 'inline-block' }}>
          <Spin spinning={loading}>
            <Progress
              type="circle"
              percent={percent}
              strokeColor={strokeColor}
              format={(p) =>
                loading ? null : (
                  <span style={{ color: '#ffffff', fontSize: '18px' }}>
                    {`${p}%`}
                  </span>
                )
              }
            />
          </Spin>
        </div>
      </Popover>
      <div className="title">{t('Storage Rate')}</div>
    </div>
  );
};

export default GatewaySlaProgress;
